"use client"

import { useCallback, useEffect, useState } from "react"

/* ─── Public hook ─── */
export const useToast = (setToasts) => {
  const addToast = useCallback((message, type = "info", duration = 4000) => {
    const id = Date.now() + Math.random()
    setToasts((prev) => [...prev, { id, message, type, duration }])
  }, [setToasts])
  const removeToast = useCallback((id) => setToasts((prev) => prev.filter((t) => t.id !== id)), [setToasts])
  return { addToast, removeToast }
}

/* ─── Container ─── */
const Toast = ({ toasts, removeToast }) => (
  <div style={containerStyle}>
    {toasts.map((t) => (
      <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
    ))}
  </div>
)

/* ─── Single item ─── */
const ToastItem = ({ toast, onClose }) => {
  const [visible, setVisible] = useState(false)
  const [progress, setProgress] = useState(100)
  const dur = toast.duration || 4000

  useEffect(() => {
    // entrance
    const t0 = setTimeout(() => setVisible(true), 10)

    // progress bar countdown
    const start = Date.now()
    const tick = setInterval(() => {
      const elapsed = Date.now() - start
      setProgress(Math.max(0, 100 - (elapsed / dur) * 100))
    }, 30)

    // auto-close
    const t1 = setTimeout(() => {
      setVisible(false)
      setTimeout(onClose, 320)
    }, dur)

    return () => { clearTimeout(t0); clearTimeout(t1); clearInterval(tick) }
  }, [])

  const cfg = {
    success: {
      icon: "✓",
      iconBg: "rgba(16,185,129,0.2)",
      iconColor: "#10b981",
      bar: "#10b981",
      bg: "linear-gradient(135deg,#064e3b 0%,#065f46 100%)",
      bgLight: "#f0fdf4",
      border: "rgba(16,185,129,0.3)",
      borderLight: "#bbf7d0",
      label: "Succès",
    },
    error: {
      icon: "✕",
      iconBg: "rgba(239,68,68,0.2)",
      iconColor: "#ef4444",
      bar: "#ef4444",
      bg: "linear-gradient(135deg,#450a0a 0%,#7f1d1d 100%)",
      bgLight: "#fff1f2",
      border: "rgba(239,68,68,0.3)",
      borderLight: "#fecdd3",
      label: "Erreur",
    },
    info: {
      icon: "i",
      iconBg: "rgba(99,102,241,0.2)",
      iconColor: "#6366f1",
      bar: "#6366f1",
      bg: "linear-gradient(135deg,#1e1b4b 0%,#312e81 100%)",
      bgLight: "#eef2ff",
      border: "rgba(99,102,241,0.3)",
      borderLight: "#c7d2fe",
      label: "Info",
    },
  }[toast.type] || {}

  const isDark = true // toasts always use dark-style backgrounds

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: "0.85rem",
        padding: "1rem 1rem 0.85rem",
        borderRadius: "14px",
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        boxShadow: "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
        overflow: "hidden",
        minWidth: "280px",
        maxWidth: "380px",
        transform: visible ? "translateX(0)" : "translateX(110%)",
        opacity: visible ? 1 : 0,
        transition: "transform 0.32s cubic-bezier(0.34,1.4,0.64,1), opacity 0.28s ease",
        fontFamily: "'Inter','Segoe UI',sans-serif",
      }}
    >
      {/* Icon circle */}
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: cfg.iconBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: cfg.iconColor,
        fontWeight: 800, fontSize: "0.95rem",
        flexShrink: 0, marginTop: "1px",
      }}>
        {cfg.icon}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, color: cfg.iconColor, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.2rem" }}>
          {cfg.label}
        </div>
        <div style={{ fontSize: "0.9rem", color: "#e2e8f0", lineHeight: 1.45, wordBreak: "break-word" }}>
          {toast.message}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={() => { setVisible(false); setTimeout(onClose, 300) }}
        style={{
          background: "rgba(255,255,255,0.08)",
          border: "none", borderRadius: "6px",
          width: 24, height: 24,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "rgba(255,255,255,0.5)",
          fontSize: "1rem", cursor: "pointer",
          flexShrink: 0, transition: "all 0.15s",
          padding: 0, lineHeight: 1,
        }}
        onMouseEnter={e => { e.target.style.background = "rgba(255,255,255,0.18)"; e.target.style.color = "#fff" }}
        onMouseLeave={e => { e.target.style.background = "rgba(255,255,255,0.08)"; e.target.style.color = "rgba(255,255,255,0.5)" }}
      >
        ×
      </button>

      {/* Progress bar */}
      <div style={{
        position: "absolute", bottom: 0, left: 0,
        height: "3px",
        width: `${progress}%`,
        background: cfg.bar,
        borderRadius: "0 3px 3px 0",
        opacity: 0.8,
        transition: "width 0.03s linear",
      }} />
    </div>
  )
}

const containerStyle = {
  position: "fixed",
  bottom: "1.5rem",
  right: "1.5rem",
  zIndex: 10000,
  display: "flex",
  flexDirection: "column",
  gap: "0.6rem",
  alignItems: "flex-end",
}

export default Toast
